import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { HOME } from "@/constants/testIds";

export default function Hero() {
  return (
    <section className="relative overflow-hidden hero-grid-bg">
      <div className="absolute top-24 -right-20 h-80 w-80 rounded-full bg-[#00A651]/10 blur-3xl" />
      <div className="absolute top-40 -left-24 h-72 w-72 rounded-full bg-[#1F1E4E]/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm mb-6"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#00A651]" />
              70+ HR modules · Built for hire-to-retire
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#1F1E4E] px-3 py-1.5 text-xs font-semibold text-white shadow-sm mb-6 ml-2"
              data-testid="hero-multilang-badge"
            >
              <span className="text-[#00A651]">●</span>
              Multilingual · English &amp; العربية
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#1F1E4E] leading-[1.05]"
            >
              The HR platform your team will<br />
              <span className="relative inline-block">
                <span className="relative z-10">actually enjoy using.</span>
                <span className="absolute left-0 bottom-1 h-3 w-full bg-[#00A651]/25 -z-0 rounded"></span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              ClockedIn HRM unifies attendance, leave, payroll and self-service into one modern, cloud-based platform — so HR spends less time on paperwork and more time on people.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link
                to="/contact?intent=book_demo"
                data-testid={HOME.heroBookDemo}
                className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3.5 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
              >
                Book a Demo <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact?intent=request_quote"
                data-testid={HOME.heroRequestQuote}
                className="inline-flex items-center gap-2 border-2 border-[#1F1E4E] text-[#1F1E4E] hover:bg-[#1F1E4E] hover:text-white font-bold rounded-lg px-6 py-3.5 transition-all"
              >
                Request a Quote
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600"
            >
              {["No credit card required", "Free onboarding & migration", "Cancel anytime"].map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[#00A651]" /> {t}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Dashboard mock */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-[#00A651]/15 via-[#1F1E4E]/5 to-transparent blur-2xl" />
              <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="h-8 bg-slate-50 border-b border-slate-200 flex items-center gap-1.5 px-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                  <span className="ml-3 text-[10px] font-semibold text-slate-400">app.clockedin.hrm</span>
                </div>
                <div className="p-5 bg-[#F8FAFC]">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Good Morning</div>
                      <div className="text-lg font-extrabold text-[#1F1E4E]">HR Dashboard</div>
                    </div>
                    <div className="h-8 w-8 rounded-full bg-[#1F1E4E] text-white flex items-center justify-center text-xs font-bold">SA</div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { l: "Present", v: "412", c: "#00A651" },
                      { l: "On Leave", v: "23", c: "#1F1E4E" },
                      { l: "Late", v: "07", c: "#F59E0B" },
                    ].map((s) => (
                      <div key={s.l} className="bg-white rounded-xl border border-slate-200 p-3">
                        <div className="text-[10px] font-bold text-slate-400 uppercase">{s.l}</div>
                        <div className="text-xl font-extrabold" style={{ color: s.c }}>{s.v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-xs font-bold text-[#1F1E4E] mb-3">Attendance this week</div>
                    <div className="flex items-end gap-2 h-24">
                      {[60, 78, 92, 84, 96, 40, 32].map((h, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full rounded-t-md bg-[#00A651]/80" style={{ height: `${h}%` }} />
                          <span className="text-[9px] font-bold text-slate-400">{["M","T","W","T","F","S","S"][i]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 bg-[#1F1E4E] rounded-xl p-4 text-white">
                    <div className="text-[10px] font-bold text-[#00A651] uppercase tracking-widest mb-1">Payroll · March</div>
                    <div className="flex items-end justify-between">
                      <div className="text-2xl font-extrabold">$247,480</div>
                      <div className="text-xs text-white/60">Processed · 100%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
